import { Plus, Pencil, Check } from "lucide-react";
import type { EngagementEvent } from "@/types/engagement";
import { formatEventDate } from "@/utils/dateUtils";
import { TYPE_CONFIG } from "./engagementConfig";
import { buildAction, buildByLine } from "./engagementUtils";

type Props = {
  event: EngagementEvent;
  showCompany: boolean;
};

export const EngagementEventCard = ({ event, showCompany }: Props) => {
  const cfg = TYPE_CONFIG[event.type] ?? TYPE_CONFIG.note;
  const Icon = cfg.icon;
  const byLine = buildByLine(event.type, event.userName);
  return (
    <div
      className="flex items-center gap-2.5 py-3 px-4 bg-white border border-gray-200 rounded-lg mb-3 last:mb-0"
      style={{ boxShadow: `inset 5px 0 0 ${cfg.color}` }}
    >
      <span className="relative shrink-0 flex items-center justify-center w-9 h-9 rounded-full" style={{ color: cfg.color, backgroundColor: cfg.backgroundColor ?? cfg.color + '18' }}>
        <Icon size={16} />
        <span className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-4 h-4 rounded-full bg-white border border-gray-200">
          {event.operation === "created" ? <Plus size={10} style={{ color: cfg.color }} /> : event.operation === "completed" || event.operation === "createdandcompleted" ? <Check size={10} style={{ color: cfg.color }} /> : <Pencil size={10} style={{ color: cfg.color }} />}
        </span>
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline flex-wrap gap-2 min-w-0 mt-1.5">
          <span className="text-xs font-semibold text-gray-900 shrink-0">{buildAction(event.type, event.operation)}</span>
          {buildByLine(event.type, event.userName) && (
            <span className="text-xs font-normal text-gray-500 shrink-0">{byLine}</span>
          )}
          {showCompany && event.type !== "prospect" && event.companyId && event.companyName && (
            <a
              href={`/Dashboard.aspx?customerid=${event.companyId}`}
              className="inline-block px-2 text-[11px] font-semibold rounded-full shrink-0 no-underline transition-colors bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100"
              onClick={e => e.stopPropagation()}
            >{event.companyName}</a>
          )}
          <span className="shrink-0 text-[11px] text-gray-400 whitespace-nowrap">{formatEventDate(event.date)}</span>
        </div>
        {(event.title || event.description) && (
          <div className="mt-0.5">
            <div className="text-[14px] text-gray-700 overflow-hidden text-ellipsis whitespace-nowrap">
              {event.link ? (
                <a
                  href={event.link}
                  target={event.type === "document" ? "_blank" : undefined}
                  rel={event.type === "document" ? "noopener noreferrer" : undefined}
                  className="text-sky-600 no-underline hover:text-sky-800"
                >
                  {event.title}
                </a>
              ) : (
                event.title
              )}
            </div>
            {event.description && (
              <div className="text-[13px] text-gray-500 overflow-hidden text-ellipsis whitespace-nowrap">
                {event.description}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
